import { useMemo } from "react";
import useQueueStore from "../stores/queue-store";
import useGetQueueConfig from "../apis/tryst/get-queue-config";
import {
  Appointment,
  DEFAULT_ACTION_CONFIG,
  QUEUE_CARD_ACTIONS,
  QUEUE_TYPE,
} from "@elixir/types";
import { getQueueTypeFromStatus } from "../utils/appointment-utils";
import { useQuery } from "@tanstack/react-query";
import { CREATE_APPT_CONFIG_QUERY_KEY } from "../constants/query-keys";
import { getCreateApptConfig } from "../utils/bridges/methods";

const useGetViewActions = ({
  rowIdx = 0,
  apt,
}: {
  rowIdx?: number;
  apt: Appointment;
}) => {
  const rightPanelQueueType = useQueueStore(
    (state) => state.rightPanelQueueType
  );
  const { data: queueConfig } = useGetQueueConfig();
  const { workflow_views } = queueConfig?.data || {};
  const { data: createApptConfig } = useQuery({
    queryKey: [CREATE_APPT_CONFIG_QUERY_KEY],
    queryFn: getCreateApptConfig,
  });
  const isServicesTurnedOn = useMemo(() => {
    if (!createApptConfig) return false;
    const serviceConfig = createApptConfig.find((c) => c.id === "service");
    return !!serviceConfig?.isShown;
  }, [createApptConfig]);

  const viewActions = useMemo(() => {
    const currentView = workflow_views?.find(
      (view) => view.viewId === rightPanelQueueType
    );
    const queueType = getQueueTypeFromStatus(apt.status);
    const defaultPrimaryActions: QUEUE_CARD_ACTIONS[] = [];

    if (queueType === QUEUE_TYPE.COMPLETED) {
      defaultPrimaryActions.push(QUEUE_CARD_ACTIONS.PRINT_RX);
    } else {
      defaultPrimaryActions.push(
        QUEUE_CARD_ACTIONS.WRITE_RX,
        QUEUE_CARD_ACTIONS.EXIT
      );
    }

    // LEGACY WAY of checking is default
    const isDefault = !currentView?.actions || currentView.actions.length === 0;
    const defaultViewIds = Object.values(QUEUE_TYPE) as string[];
    const isDefaultQueueV2 = defaultViewIds.includes(rightPanelQueueType);

    const viewActions =
      currentView?.actions && currentView.actions.length > 0
        ? currentView.actions
        : DEFAULT_ACTION_CONFIG;

    const actions = viewActions[rowIdx] || [];
    let primaryActions: string[] = [];
    const secondaryActions: string[] = [];

    actions.forEach((action) => {
      if (action.colType === "PRIMARY") {
        primaryActions.push(action.colId);
      } else {
        secondaryActions.push(action.colId);
      }
    });

    if (isDefault) {
      primaryActions = defaultPrimaryActions;
    }

    if (isDefaultQueueV2) {
      if (rowIdx === 0 && isServicesTurnedOn) {
        secondaryActions.push(QUEUE_CARD_ACTIONS.ADD_SERVICES_TO_APPOINTMENT);
      }
    }
    return [primaryActions, secondaryActions];
  }, [
    workflow_views,
    apt.status,
    rowIdx,
    rightPanelQueueType,
    isServicesTurnedOn,
  ]);

  return viewActions;
};
export default useGetViewActions;
